'use client'

import NextImage, { getImageProps } from 'next/image'
import type {
  TypeCmsMediumImage,
  TypeCmsMediumVariant,
  TypeCmsModernImage,
  TypeCmsOldImage,
  TypeCmsRasterImage,
  TypeCmsVectorImage,
} from '@/schema'
import { type FC } from 'react'
import styled from 'styled-components'

// KEEP STRING ORDER FROM LARGEST TO SMALLEST
const VARIANTS_IN_PROJECT = [
  'xl',
  'l',
  'm',
  's',
  'sliderXl',
  'sliderL',
  'sliderM',
  'sliderS',
]

type TypeVariantsKeys = (typeof VARIANTS_IN_PROJECT)[number]

type Props = {
  readonly media?: TypeCmsRasterImage | TypeCmsVectorImage
  readonly lazy?: boolean
  readonly initialInView?: boolean
  onClick?: () => void
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void
  readonly fill?: boolean
  readonly outRef?: (node?: Element) => void
  readonly className?: string
  readonly sizes?: string
  readonly quality?: number
  readonly ariaHidden?: boolean
  readonly unoptimized?: boolean
}

const Placeholder = styled.div`
  background-color: rgba(0, 0, 0, 0.15);
  width: 100%;
  height: 100%;
`

const hasValidSrc = (variant: TypeCmsMediumVariant | undefined): boolean => {
  if (!variant) return false
  if (!variant.src) return false
  if (typeof variant.src !== 'string') return false
  if (variant.src.trim() === '') return false
  return true
}

export const getMediaVariant = (
  obj: Record<string, TypeCmsMediumVariant> | undefined,
  variantNames: TypeVariantsKeys[] = VARIANTS_IN_PROJECT
): TypeCmsMediumVariant | undefined => {
  if (!obj) return undefined
  for (const key of variantNames) {
    if (key in obj) return obj[key]
  }
  return undefined
}

const Picture = ({ media, lazy, quality, ariaHidden, className }) => {
  const common = { alt: media.alt, sizes: '100vw' }
  const mobileVariant = getMediaVariant(
    media,
    [...VARIANTS_IN_PROJECT].reverse()
  )
  const desktopVariant = getMediaVariant(media, VARIANTS_IN_PROJECT)

  if (!hasValidSrc(mobileVariant) || !hasValidSrc(desktopVariant)) {
    return <Placeholder className={className} />
  }

  const { props: mobileProps } = getImageProps({
    ...common,
    ...mobileVariant.size,
    quality,
    src: mobileVariant.src,
		unoptimized: true,
  })

  const { props: desktopProps } = getImageProps({
    ...common,
    ...desktopVariant.size,
    quality,
    src: desktopVariant.src,
		unoptimized: true,
  })

  return (
    <picture>
      <source
        media='(min-width: 768px)'
        srcSet={desktopProps.src}
        height={desktopProps.height}
        width={desktopProps.width}
      />
      <source
        media='(max-width: 767px)'
        srcSet={mobileProps.src}
        height={mobileProps.height}
        width={mobileProps.width}
      />
      <img
        loading={lazy ? 'lazy' : 'eager'}
        draggable='false'
        {...(ariaHidden && { 'aria-hidden': ariaHidden })}
        alt={media?.alt ?? 'Img'}
      />
    </picture>
  )
}

function isTypeCmsVectorImage(
  media: TypeCmsRasterImage | TypeCmsVectorImage
): media is TypeCmsVectorImage {
  return media && 'type' in media && media.type === 'svg'
}

function isOldRasterImage(
  media: TypeCmsRasterImage | TypeCmsVectorImage
): media is TypeCmsOldImage {
  return (
    media && 'type' in media && media.type === 'image' && 'default' in media
  )
}

const fallBackSize = { width: 200, height: 200 }

const Image = ({
  media,
  lazy,
  fill,
  quality,
  ariaHidden,
  sizes,
  unoptimized,
  className,
  onLoad,
}) => {
  const isVector = isTypeCmsVectorImage(media)
  const variant = isVector ? media : getMediaVariant(media, VARIANTS_IN_PROJECT)

  if (!hasValidSrc(variant as TypeCmsMediumVariant)) {
    return <Placeholder className={className} />
  }

  if (!variant?.size?.width || !variant?.size?.height) {
    console.warn(
      'Image component: wrong variant size params for variant.src:',
      variant.src
    )
  }

  const sizeData = (!variant?.size?.width || !variant?.size?.height)
    ? fallBackSize
    : variant.size

  return (
    <NextImage
      src={variant.src}
      priority={!lazy}
      loading={lazy ? 'lazy' : 'eager'}
      {...(quality !== 75 && { quality })}
      alt={media.alt}
      {...(!fill && {
        height: sizeData?.height,
        width: sizeData?.width,
      })}
      {...(sizes && { sizes })}
      {...(className && { className })}
      {...(ariaHidden && { 'aria-hidden': ariaHidden })}
      {...((unoptimized || isVector || !sizeData?.width || !sizeData?.height) && { unoptimized: true })}
      {...(onLoad && { onLoad })}
    />
  )
}

const ComponentByType = {
  single: Image,
  multi: Picture,
}

const multiVariants = (
  obj: TypeCmsModernImage | undefined,
  keys: TypeVariantsKeys[]
): boolean => {
  if (!obj) return false
  const matchedKeys = keys.filter((keyname) => keyname in obj)
  return matchedKeys.length >= 2
}

const toMediaVariant = (source: TypeCmsOldImage['default']): TypeCmsMediumVariant => ({
  src: source.src,
  size: {
    height: source.size?.height,
    width: source.size?.width,
  },
})

const parseOldMedia = (data: TypeCmsOldImage): TypeCmsMediumImage['image'] => {
  if (!data?.default) return data

  const hasDistinctMobile = !!data.mobile?.src && data.mobile.src !== data.default.src

  return {
    alt: data.alt,
    xl: toMediaVariant(data.default),
    ...(hasDistinctMobile && { s: toMediaVariant(data.mobile!) }),
  }
}

export const Img: FC<Props> = ({
  media,
  lazy,
  quality = 75,
  fill = false,
  sizes,
  ariaHidden = false,
  unoptimized = false,
  className = '',
  onLoad,
}) => {
  if (!media) {
    // console.warn(
    //   '⚠️ Img component received undefined media. Product without images or image not processed yet.'
    // )
    return <Placeholder className={className} />
  }

  const mediaParsed = isOldRasterImage(media) ? parseOldMedia(media) : media

  const Component =
    ComponentByType[
    multiVariants(mediaParsed as TypeCmsModernImage, VARIANTS_IN_PROJECT) ? 'multi' : 'single'
    ]

  return (
    <Component
      media={mediaParsed}
      lazy={lazy}
      quality={quality}
      fill={fill}
      sizes={sizes}
      ariaHidden={ariaHidden}
      unoptimized={unoptimized}
      className={className}
      onLoad={onLoad}
    />
  )
}
